import { useState, useEffect, useCallback } from 'react';
import logoMark from '../assets/logo-gestion360.png';
import { BRAND } from '../config/brand';
import { IconTile } from './Icons';
import OrgDetailPage from './OrgDetailPage';
import { FEATURE_GROUPS, hasFeature } from '../lib/plans';
import { SearchBox, Chips, SelectFilter, DateRange, FilterBar, TotalsBar, matchesQuery } from './Filters';
import {
  platformListOrgs, platformCreateOrg, platformSetStatus, platformSetPlan,
  platformSetOwnerPassword, platformAudit, platformOrgUsage,
  platformPlans, platformUpsertPlan, platformSetOrgEntitlements, platformOrgEntitlements,
  platformOrgDetails, platformUpdateOrg, platformOrgPeople, platformUpdatePerson, platformSetPersonPassword,
  platformGetSettings, platformSetSetting, platformListTrials, platformSetTrial, welcomeOwner, getPublicPlans, platformSetPlanPrice, platformExportOrg, downloadJson,
} from '../lib/org';

const STATUS = {
  trial:     { label: 'Prueba',     cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  active:    { label: 'Activa',     cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  suspended: { label: 'Suspendida', cls: 'bg-red-50 text-red-600 border-red-200' },
  cancelled: { label: 'Cancelada',  cls: 'bg-gray-100 text-gray-500 border-gray-200' },
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const ACTION_LABEL = {
  org_created: 'Organización creada', org_status: 'Cambio de estado',
  org_plan: 'Cambio de plan', org_edit: 'Datos de la organización editados', person_created: 'Persona creada', person_role: 'Rol cambiado', person_edit: 'Persona editada', person_password_reset: 'Clave de una persona restablecida', user_email_changed: 'Correo de ingreso cambiado', owner_password_reset: 'Clave del dueño restablecida',
};

const daysSince = (d) => (d ? Math.floor((Date.now() - new Date(d).getTime()) / 86400000) : null);

const SETTINGS = [
  ['email_from_address', 'Mail remitente de la plataforma', 'no-responder@tudominio.com', 'Tiene que estar verificado en SMTP2GO. Es el único remitente: cada organización pone su nombre y su mail de respuesta.'],
  ['email_from_name', 'Nombre de la plataforma en los mails', 'Gestión360', ''],
  ['legal_name', 'Razón social o nombre del titular', 'Ej.: Shalom Levy / Gestión360 S.A.', 'Aparece en los Términos y la Política de Privacidad.'],
  ['legal_tax_id', 'CUIT', '20-12345678-9', ''],
  ['legal_address', 'Domicilio legal', 'Calle 123, Ciudad Autónoma de Buenos Aires', ''],
  ['contact_email', 'Mail de contacto, privacidad y cancelaciones', 'contacto@tudominio.com', 'Lo ven los clientes en los Términos, la Política y los avisos de vencimiento.'],
  ['app_url', 'Dirección pública de la app', 'https://…', 'Se usa en los links de las invitaciones. Cambiala cuando tengas dominio propio.'],
];
const genPass = () => {
  const c = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const a = new Uint32Array(12); crypto.getRandomValues(a);
  return Array.from(a, (n) => c[n % c.length]).join('');
};
const TRIAL_STATUS = { new: 'Nueva', contacted: 'Contactada', converted: 'Convertida', discarded: 'Descartada' };

const numOrEmpty = (v) => v.replace(/\D/g, '');
const slugify = (t) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 30);

function FeatureToggles({ features, onChange }) {
  return (
    <div className="space-y-3">
      {FEATURE_GROUPS.map((g) => (
        <div key={g.id}>
          <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">{g.label}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {g.items.map((f) => {
              const on = hasFeature(features, f.key);
              return (
                <button type="button" key={f.key} onClick={() => onChange({ ...features, [f.key]: !on })}
                  className={`flex items-center justify-between gap-2 px-3 py-2 rounded-xl border text-left text-xs font-semibold transition-colors ${on ? 'bg-sky-50 border-sky-200 text-sky-800' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
                  <span>{f.label}</span>
                  <span className={`w-8 h-4.5 h-[18px] rounded-full relative flex-shrink-0 ${on ? 'bg-sky-600' : 'bg-gray-300'}`}>
                    <span className={`absolute top-[2px] w-3.5 h-3.5 rounded-full bg-white transition-all ${on ? 'left-[16px]' : 'left-[2px]'}`} />
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function PlanEditor({ plan, isNew, onSaved, onCancel, onOk, onError }) {
  const [p, setP] = useState(plan);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(!!isNew);
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const inp = 'w-full border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white';
  const save = async (extra = {}) => {
    setBusy(true);
    try { await platformUpsertPlan({ ...p, ...extra }); onOk(isNew ? `Plan ${p.name} creado` : `Plan ${p.name} guardado`); await onSaved(); if (isNew) onCancel(); }
    catch (e) { onError(e.message || 'No se pudo guardar'); }
    setBusy(false);
  };
  return (
    <div className={`bg-white rounded-3xl border shadow-sm p-5 ${p.archived ? 'border-gray-200 opacity-70' : 'border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-900 truncate">{p.name || 'Plan nuevo'} {p.hot && <span className="text-[10px] font-bold text-sky-700 bg-sky-50 px-2 py-0.5 rounded-full ml-1">Destacado</span>}
            {p.archived && <span className="text-[10px] font-bold text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full ml-1">Archivado</span>}
            {!p.is_public && !p.archived && <span className="text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full ml-1">Privado</span>}</p>
          <p className="text-xs text-gray-400">{p.max_employees ? `${p.max_employees} personas` : 'Personas ilimitadas'} · {p.max_locations ? `${p.max_locations} sede(s)` : 'Sedes ilimitadas'}{!isNew && ` · ${p.orgs || 0} organización(es)`}</p>
        </div>
        {!isNew && <button onClick={() => setOpen(!open)} className="text-xs font-bold text-sky-600 flex-shrink-0">{open ? 'Cerrar' : 'Editar'}</button>}
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-gray-100 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="text-xs text-gray-500">Nombre
              <input className={inp} value={p.name} maxLength={40} onChange={(e) => { set('name', e.target.value); if (isNew) set('id', slugify(e.target.value)); }} /></label>
            <label className="text-xs text-gray-500">Código interno {isNew ? '' : '(no se puede cambiar)'}
              <input className={`${inp} ${isNew ? '' : 'bg-gray-50 text-gray-400'}`} value={p.id} disabled={!isNew} onChange={(e) => set('id', slugify(e.target.value))} placeholder="ej.: pyme" /></label>
            <label className="text-xs text-gray-500">Frase corta (ej.: Para empezar)
              <input className={inp} value={p.who || ''} maxLength={40} onChange={(e) => set('who', e.target.value)} /></label>
            <label className="text-xs text-gray-500">Orden en la lista
              <input className={inp} inputMode="numeric" value={p.sort_order ?? ''} onChange={(e) => set('sort_order', numOrEmpty(e.target.value))} /></label>
          </div>
          <label className="text-xs text-gray-500 block">Descripción (se muestra en /precios)
            <textarea className={inp} rows={2} maxLength={300} value={p.description || ''} onChange={(e) => set('description', e.target.value)} /></label>
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Topes (vacío = sin tope)</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-xs text-gray-500">Personas
                <input className={inp} inputMode="numeric" placeholder="Sin tope" value={p.max_employees ?? ''} onChange={(e) => set('max_employees', numOrEmpty(e.target.value))} /></label>
              <label className="text-xs text-gray-500">Sedes
                <input className={inp} inputMode="numeric" placeholder="Sin tope" value={p.max_locations ?? ''} onChange={(e) => set('max_locations', numOrEmpty(e.target.value))} /></label>
            </div>
          </div>
          <FeatureToggles features={p.features || {}} onChange={(f) => set('features', f)} />
          <div>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-1.5">Precio (vacío = "Consultá el precio")</p>
            <div className="grid grid-cols-6 gap-2">
              <input type="number" min="0" placeholder="Precio" value={p.price_amount ?? ''} onChange={(e) => set('price_amount', e.target.value)} className="col-span-2 border border-gray-200 rounded-xl px-3 py-2 text-sm" />
              <select value={p.price_currency || 'ARS'} onChange={(e) => set('price_currency', e.target.value)} className="col-span-1 border border-gray-200 rounded-xl px-2 py-2 text-sm bg-white"><option value="ARS">ARS</option><option value="USD">USD</option></select>
              <input placeholder="por mes" maxLength={60} value={p.price_note || ''} onChange={(e) => set('price_note', e.target.value)} className="col-span-3 border border-gray-200 rounded-xl px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-gray-600">
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!p.hot} onChange={(e) => set('hot', e.target.checked)} /> Destacar como "el más elegido"</label>
            <label className="flex items-center gap-1.5"><input type="checkbox" checked={p.is_public !== false} onChange={(e) => set('is_public', e.target.checked)} /> Mostrar en la página pública /precios</label>
          </div>
          <div className="flex gap-2">
            <button onClick={() => save()} disabled={busy || !p.id || !p.name} className="flex-1 py-2.5 rounded-xl text-xs font-bold text-white bg-sky-700 disabled:opacity-50">{busy ? 'Guardando…' : isNew ? 'Crear plan' : 'Guardar cambios'}</button>
            {isNew ? <button onClick={onCancel} className="px-4 rounded-xl text-xs font-bold text-gray-600 border border-gray-200">Cancelar</button>
              : <button onClick={() => save({ archived: !p.archived })} disabled={busy} className="px-4 rounded-xl text-xs font-bold text-gray-600 border border-gray-200 disabled:opacity-50">{p.archived ? 'Reactivar' : 'Archivar'}</button>}
          </div>
          {!isNew && <p className="text-[11px] text-gray-400">Los cambios de topes y funciones se aplican enseguida a todas las organizaciones con este plan (salvo las que tengan ajustes propios).</p>}
        </div>
      )}
    </div>
  );
}

function PlansTab({ plans, onChanged, onOk, onError }) {
  const [creating, setCreating] = useState(false);
  const blank = { id: '', name: '', who: '', description: '', hot: false, is_public: true, archived: false, max_employees: '', max_locations: '', features: {}, sort_order: (plans.length + 1) * 10, price_amount: '', price_currency: 'ARS', price_note: '' };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-gray-400">Creá, editá y archivá planes. Cada organización puede además tener ajustes propios (en Organizaciones → Gestionar).</p>
        {!creating && <button onClick={() => setCreating(true)} className="text-xs font-bold text-white px-3 py-2 rounded-xl bg-sky-700 whitespace-nowrap">+ Nuevo plan</button>}
      </div>
      {creating && <PlanEditor plan={blank} isNew onSaved={onChanged} onCancel={() => setCreating(false)} onOk={onOk} onError={onError} />}
      {plans.map((p) => <PlanEditor key={p.id + (p.name || '')} plan={p} onSaved={onChanged} onOk={onOk} onError={onError} />)}
    </div>
  );
}

function SettingsTab({ onOk, onError }) {
  const [vals, setVals] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { platformGetSettings().then(setVals).catch((e) => onError(e.message)); }, []);
  if (!vals) return <p className="text-sm text-gray-400 text-center py-10">Cargando…</p>;
  const save = async () => {
    setBusy(true);
    try { for (const [k] of SETTINGS) await platformSetSetting(k, vals[k] || '');
      await platformSetSetting('self_signup', vals.self_signup === 'on' ? 'on' : 'off');
      await platformSetSetting('self_signup_days', vals.self_signup_days || ''); onOk('Ajustes guardados'); }
    catch (e) { onError(e.message); }
    setBusy(false);
  };
  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-4">
      <h3 className="font-bold text-gray-900 text-sm">Correo y dirección de la plataforma</h3>
      {SETTINGS.map(([k, label, ph, help]) => (
        <label key={k} className="block text-xs font-semibold text-gray-500">{label}
          <input value={vals[k] || ''} onChange={(e) => setVals({ ...vals, [k]: e.target.value })} placeholder={ph} className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
          {help && <span className="block text-[10px] font-normal text-gray-400 mt-1">{help}</span>}
        </label>
      ))}
      <div className="rounded-2xl border border-gray-100 p-4 space-y-3">
        <div><p className="text-sm font-bold text-gray-800">Alta directa con prueba gratis</p>
          <p className="text-xs text-gray-400">Activada: quien entra al link principal crea su cuenta solo y la prueba arranca sin que la apruebes. Desactivada: queda el pedido de prueba con tu aprobación, como antes.</p></div>
        <button type="button" onClick={() => setVals({ ...vals, self_signup: vals.self_signup === 'on' ? 'off' : 'on' })}
          className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border text-left text-sm font-bold ${vals.self_signup === 'on' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-gray-50 border-gray-100 text-gray-500'}`}>
          <span>{vals.self_signup === 'on' ? 'Activada: crean la cuenta solos' : 'Desactivada: piden la prueba y vos aprobás'}</span>
          <span className={`w-9 h-5 rounded-full relative flex-shrink-0 ${vals.self_signup === 'on' ? 'bg-emerald-500' : 'bg-gray-300'}`}><span className={`absolute top-[2px] w-4 h-4 rounded-full bg-white transition-all ${vals.self_signup === 'on' ? 'left-[18px]' : 'left-[2px]'}`} /></span>
        </button>
        <label className="block text-xs font-semibold text-gray-500">Días de prueba gratis
          <input inputMode="numeric" value={vals.self_signup_days || ''} onChange={(e) => setVals({ ...vals, self_signup_days: e.target.value.replace(/\D/g, '').slice(0, 2) })} placeholder="14" className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
          <span className="block text-[10px] font-normal text-gray-400 mt-1">Entre 1 y 60. Vacío: 14. Se usa el plan que elija la persona en la página de precios; el máximo es 15 altas por hora.</span>
        </label>
      </div>
      <button onClick={save} disabled={busy} className="w-full py-2.5 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar'}</button>
    </div>
  );
}

function MetricsTab({ orgs, trials }) {
  const [plans, setPlans] = useState([]);
  useEffect(() => { getPublicPlans().then(setPlans).catch(() => {}); }, []);
  const price = Object.fromEntries(plans.map((p) => [p.id, p]));
  const mrr = {};
  let unpriced = 0;
  orgs.filter((o) => o.status === 'active').forEach((o) => {
    const p = price[o.plan_id];
    if (p && p.price_amount > 0) mrr[p.price_currency || 'ARS'] = (mrr[p.price_currency || 'ARS'] || 0) + Number(p.price_amount);
    else unpriced++;
  });
  const money = (c, v) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: c, maximumFractionDigits: 0 }).format(v);
  const asked = trials.length;
  const conv = trials.filter((t) => t.status === 'converted').length;
  const byPlan = plans.map((p) => [p.name, orgs.filter((o) => o.plan_id === p.id && o.status !== 'suspended').length]);
  const cards = [
    ['Ingreso mensual (MRR)', Object.keys(mrr).length ? Object.entries(mrr).map(([c, v]) => money(c, v)).join(' + ') : '—'],
    ['Pagan hoy', orgs.filter((o) => o.status === 'active').length],
    ['Solicitudes de prueba', asked],
    ['Conversión', asked ? `${Math.round((conv / asked) * 100)}% (${conv}/${asked})` : '—'],
    ['Suspendidas', orgs.filter((o) => o.status === 'suspended').length],
  ];
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {cards.map(([l, v]) => (
          <div key={l} className="bg-white rounded-3xl border border-gray-100 p-4 text-center">
            <p className="text-xl font-black text-sky-700 break-words">{v}</p>
            <p className="text-xs text-gray-400 mt-0.5">{l}</p>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-3xl border border-gray-100 p-4">
        <p className="text-xs font-bold text-gray-500 mb-2">Organizaciones por plan</p>
        {byPlan.map(([n, c]) => <div key={n} className="flex justify-between text-sm py-1"><span className="text-gray-700">{n}</span><b>{c}</b></div>)}
      </div>
      {unpriced > 0 && <p className="text-xs text-amber-600">{unpriced} organización(es) activa(s) sin precio definido en su plan: el MRR no las incluye. Definí los precios en Planes.</p>}
      <p className="text-[11px] text-gray-400">El MRR suma el precio de lista del plan de cada organización activa; no refleja descuentos ni cobros reales.</p>
    </div>
  );
}

function TrialsTab({ trials, onChange, onError, onConvert }) {
  if (!trials.length) return <p className="text-sm text-gray-400 text-center py-10">Todavía no hay solicitudes de prueba.</p>;
  const [q, setQ] = useState('');
  const [st, setSt] = useState('all');
  const base = trials.filter((t) => matchesQuery(q, t.org_name, t.name, t.email, t.phone, t.message));
  const list = base.filter((t) => st === 'all' || t.status === st);
  const set = async (id, st2) => { try { await platformSetTrial(id, st2); await onChange(); } catch (e) { onError(e.message); } };
  return (
    <div className="space-y-3">
      <TotalsBar items={[
        {label:'Solicitudes',value:base.length,tone:'sky'},
        {label:'Nuevas',value:base.filter((t) => t.status === 'new').length,tone:'amber'},
        {label:'Convertidas',value:base.filter((t) => t.status === 'converted').length,tone:'green'},
        {label:'Conversión',value:base.length ? `${Math.round(100 * base.filter((t) => t.status === 'converted').length / base.length)}%` : '—'},
      ]} />
      <FilterBar shown={list.length} total={trials.length} noun="solicitudes" active={!!(q || st !== 'all')} onClear={() => { setQ(''); setSt('all'); }}>
        <SearchBox value={q} onChange={setQ} placeholder="Buscar por organización, nombre o email…" />
        <Chips value={st} onChange={setSt} options={[{ id: 'all', label: 'Todas', count: base.length }, ...Object.entries(TRIAL_STATUS).map(([id, label]) => ({ id, label, count: base.filter((t) => t.status === id).length }))]} />
      </FilterBar>
      {list.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Ninguna solicitud coincide con los filtros.</p>}
      {list.map((t) => (
        <div key={t.id} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="font-bold text-gray-900 truncate">{t.org_name}</p>
              <p className="text-xs text-gray-500 truncate">{t.name} · <a className="text-sky-600" href={`mailto:${t.email}`}>{t.email}</a>{t.phone ? ` · ${t.phone}` : ''}</p>
            </div>
            {['new', 'contacted'].includes(t.status) && (
              <button onClick={() => onConvert(t)} className="text-xs font-bold text-white px-3 py-1.5 rounded-lg bg-sky-700 whitespace-nowrap">Crear organización</button>
            )}
            <select value={t.status} onChange={(e) => set(t.id, e.target.value)} className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white">
              {Object.entries(TRIAL_STATUS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">{fmtDate(t.created_at)}{t.plan_id ? ` · Plan de interés: ${t.plan_id}` : ''}</p>
          {t.message && <p className="text-xs text-gray-600 mt-2">{t.message}</p>}
        </div>
      ))}
    </div>
  );
}


function OrgRow({ org, usage, onOpen, onOk }) {
  const st = STATUS[org.status] || STATUS.active;

  return (
    <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-900 truncate">{org.name}</p>
          <p className="text-xs text-gray-400 truncate">{org.owner_email || 'Sin administrador'}</p>
        </div>
        <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full border ${st.cls}`}>{st.label}</span>
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-3 text-xs text-gray-500">
        <span>Plan <b className="text-gray-800 capitalize">{org.plan_id}</b></span>
        <span>Personas <b className="text-gray-800">{org.employees}{org.max_employees ? ` / ${org.max_employees}` : ' / ∞'}</b></span>
        <span>Alta {fmtDate(org.created_at)}</span>
        {org.status === 'trial' && <span>Prueba hasta {fmtDate(org.trial_ends_at)}</span>}
        {org.status === 'trial' && org.trial_ends_at && (() => {
          const left = Math.ceil((new Date(org.trial_ends_at).getTime() - Date.now()) / 86400000);
          return left <= 3 ? <span className="font-bold text-amber-600">⏳ Vence {left <= 0 ? 'hoy' : `en ${left} día${left === 1 ? '' : 's'}`}</span> : null;
        })()}
      </div>
      {usage && (() => {
        const d = daysSince(usage.last_activity);
        const idle = ['trial', 'active'].includes(org.status) && (d === null || d >= 14);
        return (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-1.5 text-xs text-gray-500">
            <span>Marcaciones este mes <b className="text-gray-800">{usage.checkins_month}</b></span>
            <span>Mes anterior <b className="text-gray-800">{usage.checkins_prev_month}</b></span>
            <span>Último uso <b className="text-gray-800">{usage.last_activity ? fmtDate(usage.last_activity) : 'nunca'}</b></span>
            {idle && <span className="font-bold text-amber-600">⚠ Sin actividad{d !== null ? ` hace ${d} días` : ''}</span>}
          </div>
        );
      })()}
      {org.slug && (
        <p className="text-[11px] text-gray-400 mt-1.5 truncate">
          Ingreso: {window.location.origin}/o/{org.slug}{' '}
          <button onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/o/${org.slug}`).then(() => onOk('Enlace copiado'))} className="font-bold text-sky-600">Copiar</button>
        </p>
      )}
      <button onClick={() => onOpen(org.id)} className="mt-3 text-xs font-bold text-sky-600">Abrir y configurar →</button>
    </div>
  );
}

export default function PlatformPanel({ onBack, onLogout, path = '/plataforma', go = () => {} }) {
  const [orgs, setOrgs] = useState([]);
  const [usage, setUsage] = useState({});
  const [trials, setTrials] = useState([]);
  const [fromTrial, setFromTrial] = useState(null);
  const [audit, setAudit] = useState([]);
  const [plans, setPlans] = useState([]);
  const [ents, setEnts] = useState({});
  const [tab, setTab] = useState('orgs');
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', plan: 'esencial', ownerName: '', ownerEmail: '', ownerPassword: '', trialDays: 14 });
  const [saving, setSaving] = useState(false);
  const [oQ, setOQ] = useState('');
  const [oStatus, setOStatus] = useState('all');
  const [oPlan, setOPlan] = useState('all');
  const [aQ, setAQ] = useState('');
  const [aOrg, setAOrg] = useState('all');
  const [aAct, setAAct] = useState('all');
  const [aFrom, setAFrom] = useState('');
  const [aTo, setATo] = useState('');

  const flash = (text, kind = 'ok') => { setMsg({ text, kind }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    try {
      const [o, a, u, pl, en] = await Promise.all([platformListOrgs(), platformAudit(60), platformOrgUsage().catch(() => []), platformPlans().catch(() => []), platformOrgEntitlements().catch(() => ({}))]);
      platformListTrials().then(setTrials).catch(() => {});
      setOrgs(o); setAudit(a); setPlans(pl); setEnts(en); setUsage(Object.fromEntries(u.map((x) => [x.organization_id, x])));
    } catch (e) { flash(e.message || 'No se pudo cargar', 'error'); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = document.title; document.title = 'Plataforma · Gestión360'; return () => { document.title = t; }; }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const orgId = await platformCreateOrg({ ...form, trialDays: parseInt(form.trialDays, 10) || 0 });
      if (fromTrial) {
        await platformSetTrial(fromTrial.id, 'converted');
        try { await welcomeOwner(orgId); flash(`Organización creada e invitación enviada a ${form.ownerEmail}`); }
        catch (me) { flash(`Organización creada, pero el mail no salió (${me.message}). Clave inicial: ${form.ownerPassword}`, 'error'); }
        setFromTrial(null);
      } else flash('Organización creada. Pasale el email y la clave al dueño.');
      setShowNew(false);
      setForm({ name: '', plan: 'esencial', ownerName: '', ownerEmail: '', ownerPassword: '', trialDays: 14 });
      await load();
    } catch (err) { flash(err.message || 'No se pudo crear', 'error'); }
    setSaving(false);
  };

  const count = (s) => orgs.filter((o) => o.status === s).length;
  const totalPeople = orgs.reduce((a, o) => a + (o.employees || 0), 0);
  const nameById = Object.fromEntries(orgs.map((o) => [o.id, o.name]));
  const oBase = orgs.filter((o) => matchesQuery(oQ, o.name, o.slug, o.owner_email) && (oPlan === 'all' || o.plan_id === oPlan));
  const oList = oBase.filter((o) => oStatus === 'all' || o.status === oStatus);
  const detailId = (path.match(/^\/plataforma\/org\/([0-9a-f-]{36})\/?$/i) || [])[1];
  const detailOrg = detailId ? orgs.find((o) => o.id === detailId) : null;
  const aList = audit.filter((a) => matchesQuery(aQ, ACTION_LABEL[a.action] || a.action, nameById[a.organization_id], a.detail?.plan, a.detail?.status)
    && (aOrg === 'all' || a.organization_id === aOrg) && (aAct === 'all' || a.action === aAct)
    && (!aFrom || a.at.slice(0, 10) >= aFrom) && (!aTo || a.at.slice(0, 10) <= aTo));

  return (
    <div className="min-h-screen bg-gray-50">
      <div style={{ background: 'linear-gradient(135deg,#0f172a 0%,#1e293b 100%)' }}>
        <div className="max-w-4xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logoMark} alt={BRAND.product} className="w-8 h-8 rounded-2xl" />
            <div>
              <p className="text-white font-bold text-base leading-tight">{BRAND.product}</p>
              <p className="text-[11px] text-white/50 leading-tight">Administración de la plataforma</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {onBack && <button onClick={onBack} className="text-xs text-white/70 px-3 py-1.5 rounded-xl hover:bg-white/10">Mi organización</button>}
            <button onClick={onLogout} className="text-xs text-white/60 px-3 py-1.5 rounded-xl hover:bg-white/10">Salir</button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-5 py-6 space-y-5">
        {msg && (
          <div className={`rounded-2xl px-4 py-3 text-sm font-medium border ${msg.kind === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>{msg.text}</div>
        )}

        {detailOrg ? (
          <OrgDetailPage key={detailOrg.id} org={detailOrg} usage={usage[detailOrg.id]} plans={plans} ent={ents[detailOrg.id]} onBack={() => go('/plataforma')}
            onChange={load} onError={(t) => flash(t, 'error')} onOk={(t) => flash(t)} />
        ) : loading && detailId ? <p className="text-sm text-gray-400">Cargando…</p> : (<>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[['Organizaciones', orgs.length], ['Activas', count('active')], ['En prueba', count('trial')], ['Personas', totalPeople]].map(([l, v]) => (
            <div key={l} className="bg-white rounded-3xl border border-gray-100 p-4 text-center">
              <p className="text-3xl font-black text-sky-700">{v}</p>
              <p className="text-xs text-gray-400 mt-0.5">{l}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="grid grid-cols-6 gap-2 w-full sm:max-w-xl">
            {[['orgs', 'Organizaciones'], ['trials', 'Solicitudes'], ['metrics', 'Métricas'], ['plans', 'Planes'], ['settings', 'Ajustes'], ['audit', 'Auditoría']].map(([id, l]) => {
              const n = id === 'trials' ? trials.filter((t) => t.status === 'new').length : 0;
              return (
                <button key={id} onClick={() => setTab(id)} aria-label={l}
                  className={`relative flex flex-col items-center gap-1 pt-2.5 pb-2 px-0.5 rounded-2xl border transition-all active:scale-95 ${tab === id ? 'border-sky-300 bg-sky-50 shadow-sm' : 'border-gray-100 bg-white hover:border-sky-200'}`}>
                  {n > 0 && <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">{n}</span>}
                  <IconTile name={id} size="sm" />
                  <span className={`text-[10px] font-semibold leading-tight text-center ${tab === id ? 'text-sky-800' : 'text-gray-500'}`}>{l}</span>
                </button>
              );
            })}
          </div>
          {tab === 'orgs' && (
            <button onClick={() => { if (showNew) setFromTrial(null); setShowNew(!showNew); }} className="px-4 py-2.5 rounded-xl text-xs font-bold text-white" style={{ background: 'var(--brand-grad)' }}>
              {showNew ? 'Cancelar' : '+ Nueva organización'}
            </button>
          )}
        </div>

        {tab === 'trials' && <TrialsTab onConvert={(t) => {
          setForm({ name: t.org_name, plan: t.plan_id || 'profesional', ownerName: t.name, ownerEmail: t.email, ownerPassword: genPass(), trialDays: 14 });
          setFromTrial(t); setShowNew(true); setTab('orgs');
        }} trials={trials} onChange={load} onError={(t) => flash(t, 'error')} />}
        {tab === 'metrics' && <MetricsTab orgs={orgs} trials={trials} />}
        {tab === 'plans' && <PlansTab plans={plans} onChanged={load} onOk={(t) => flash(t)} onError={(t) => flash(t, 'error')} />}
        {tab === 'settings' && <SettingsTab onOk={(t) => flash(t)} onError={(t) => flash(t, 'error')} />}

        {tab === 'orgs' && showNew && (
          <form onSubmit={create} className="bg-white rounded-3xl border border-gray-100 shadow-sm p-5 space-y-3">
            <h3 className="font-bold text-gray-900 text-sm">Nueva organización</h3>
            <input required placeholder="Nombre de la institución o comercio" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
            <div className="grid grid-cols-2 gap-2">
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white">
                {plans.filter((p) => !p.archived).map((p) => <option key={p.id} value={p.id}>Plan {p.name}</option>)}
              </select>
              <input type="number" min="0" placeholder="Días de prueba" value={form.trialDays} onChange={(e) => setForm({ ...form, trialDays: e.target.value })} className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
            </div>
            <p className="text-xs text-gray-400 pt-1">Administrador principal de esa organización</p>
            <input required placeholder="Nombre del dueño" value={form.ownerName} onChange={(e) => setForm({ ...form, ownerName: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
            <input required type="email" placeholder="Email del dueño" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
            <input required minLength={8} placeholder="Clave inicial (mín. 8)" value={form.ownerPassword} onChange={(e) => setForm({ ...form, ownerPassword: e.target.value })} className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm" />
            <button disabled={saving} className="w-full py-3 rounded-xl text-sm font-bold text-white bg-sky-700 disabled:opacity-50">{saving ? 'Creando…' : 'Crear organización'}</button>
          </form>
        )}

        {tab === 'orgs' && (loading ? <p className="text-sm text-gray-400 text-center py-10">Cargando…</p>
          : orgs.length === 0 ? <p className="text-sm text-gray-400 text-center py-10">Todavía no hay organizaciones.</p>
          : <div className="space-y-3">
            <TotalsBar items={[
              {label:'Organizaciones',value:oList.length,tone:'sky'},
              {label:'Personas',value:oList.reduce((a, o) => a + (o.employees || 0), 0),tone:'green'},
              {label:'Marcaciones este mes',value:oList.reduce((a, o) => a + (usage[o.id]?.checkins_month || 0), 0)},
              {label:'En prueba',value:oList.filter((o) => o.status === 'trial').length,tone:'amber'},
            ]} />
            <FilterBar shown={oList.length} total={orgs.length} noun="organizaciones" active={!!(oQ || oStatus !== 'all' || oPlan !== 'all')} onClear={() => { setOQ(''); setOStatus('all'); setOPlan('all'); }}>
              <div className="flex gap-2 flex-wrap">
                <SearchBox className="flex-1 min-w-[180px]" value={oQ} onChange={setOQ} placeholder="Buscar organización…" />
                <SelectFilter value={oPlan} onChange={setOPlan} options={plans.map((p) => ({ value: p.id, label: `Plan ${p.name}` }))} allLabel="Todos los planes" />
              </div>
              <Chips value={oStatus} onChange={setOStatus} options={[{ id: 'all', label: 'Todas', count: oBase.length }, { id: 'active', label: 'Activas', count: oBase.filter((o) => o.status === 'active').length }, { id: 'trial', label: 'En prueba', count: oBase.filter((o) => o.status === 'trial').length }, { id: 'suspended', label: 'Suspendidas', count: oBase.filter((o) => o.status === 'suspended').length }]} />
            </FilterBar>
            {oList.length === 0 && <p className="text-sm text-gray-400 text-center py-8">Ninguna organización coincide con los filtros.</p>}
            {oList.map((o) => <OrgRow key={o.id} org={o} usage={usage[o.id]} onOpen={(id) => go(`/plataforma/org/${id}`)} onOk={(t) => flash(t)} />)}</div>)}

        {tab === 'audit' && (
          <div className="space-y-3">
          <FilterBar shown={aList.length} total={audit.length} noun="movimientos" active={!!(aQ || aOrg !== 'all' || aAct !== 'all' || aFrom || aTo)} onClear={() => { setAQ(''); setAOrg('all'); setAAct('all'); setAFrom(''); setATo(''); }}>
            <div className="flex gap-2 flex-wrap">
              <SearchBox className="flex-1 min-w-[180px]" value={aQ} onChange={setAQ} placeholder="Buscar en la auditoría…" />
              <SelectFilter value={aOrg} onChange={setAOrg} options={orgs.map((o) => ({ value: o.id, label: o.name }))} allLabel="Todas las organizaciones" />
              <SelectFilter value={aAct} onChange={setAAct} options={[...new Set(audit.map((a) => a.action))].map((k) => ({ value: k, label: ACTION_LABEL[k] || k }))} allLabel="Todas las acciones" />
            </div>
            <DateRange from={aFrom} to={aTo} onChange={({ from, to }) => { setAFrom(from); setATo(to); }} />
          </FilterBar>
          <div className="bg-white rounded-3xl border border-gray-100 divide-y divide-gray-50">
            {aList.length === 0 && <p className="text-sm text-gray-400 text-center py-10">Sin movimientos.</p>}
            {aList.map((a) => (
              <div key={a.id} className="px-5 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800">{ACTION_LABEL[a.action] || a.action}</p>
                  <p className="text-xs text-gray-400 truncate">{nameById[a.organization_id] || '—'}{a.detail?.plan ? ` · plan ${a.detail.plan}` : ''}{a.detail?.status ? ` · ${a.detail.status}` : ''}</p>
                </div>
                <span className="text-[11px] text-gray-400 flex-shrink-0">{new Date(a.at).toLocaleString('es-AR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
              </div>
            ))}
          </div>
          </div>
        )}
        </>)}
      </div>
    </div>
  );
}
